import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { submitSaleRegistration, fetchAcceptJsConfig } from '../services/registration.service';
import { getPaymentNonce } from '../services/acceptjs';
import toast from 'react-hot-toast';

// Sentinel "auction" id used ONLY to trigger the shared card-auth flow during
// account setup. The card-auth callable vaults the card to users/{uid}.authnet
// (same shape registerForSale writes) so the 6-month fast-path applies at later
// real-sale registration. It does NOT grant bidding access to any real sale —
// that still requires per-sale registration on that auction.
const ACCOUNT_SETUP_AUCTION_ID = 'account-setup';

const BidderRegistration: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const [loading, setLoading] = useState(false);

  // Card-auth modal state (reuses the proven SalePage Accept.js flow).
  const [showCardForm, setShowCardForm] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [expMonth, setExpMonth] = useState('');
  const [expYear, setExpYear] = useState('');
  const [cardCode, setCardCode] = useState('');
  const [cardSubmitting, setCardSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    displayName: '',
    phoneNumber: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'United States',
  });

  // Load existing user data when available
  useEffect(() => {
    if (userProfile) {
      setFormData({
        displayName: userProfile.displayName || '',
        phoneNumber: userProfile.phoneNumber || '',
        addressLine1: userProfile.addressLine1 || '',
        addressLine2: userProfile.addressLine2 || '',
        city: userProfile.city || '',
        state: userProfile.state || '',
        zipCode: userProfile.zipCode || '',
        country: userProfile.country || 'United States',
      });
    }
  }, [userProfile]);

  // Redirect if not signed in
  useEffect(() => {
    if (!currentUser) {
      toast.error('Please sign in first');
      navigate('/signup');
    }
  }, [currentUser, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!currentUser) {
      toast.error('Please sign in first');
      navigate('/signup');
      return;
    }

    // Validation
    if (!formData.displayName || !formData.phoneNumber || !formData.addressLine1 || !formData.city || !formData.state || !formData.zipCode) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      // Update existing user profile in Firestore
      await updateDoc(doc(db, 'users', currentUser.uid), {
        displayName: formData.displayName,
        phoneNumber: formData.phoneNumber,
        addressLine1: formData.addressLine1,
        addressLine2: formData.addressLine2,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        country: formData.country,
        updatedAt: new Date(),
      });

      // Profile saved. We do NOT run a "sale registration" here: 'account-setup' is not a
      // real sale, so registerForSale now rejects it server-side — and doing so used to burn
      // a REAL $0 card auth, write a junk saleRegistrations/account-setup_{uid} doc, and send
      // a bogus "You're registered — the sale" email with a broken link. Card verification +
      // bidding access happen at REAL per-sale registration (SalePage / lot detail), which is
      // the only place that grants access anyway.
      toast.success('Profile saved.');
      navigate('/browse');
    } catch (error: any) {
      console.error('Update error:', error);
      toast.error('Failed to update profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCardSubmit = async () => {
    if (!cardNumber.trim() || !expMonth.trim() || !expYear.trim() || !cardCode.trim()) {
      toast.error('Enter card number, expiry month/year, and CVV.');
      return;
    }
    setCardSubmitting(true);
    try {
      // Card fields go browser -> Authorize.net (Accept.js). We only ever hold
      // the returned one-time nonce, never the PAN.
      const cfg = await fetchAcceptJsConfig();
      const nonce = await getPaymentNonce(cfg, {
        cardNumber: cardNumber.trim(),
        month: expMonth.trim().padStart(2, '0'),
        year: expYear.trim().length === 2 ? `20${expYear.trim()}` : expYear.trim(),
        cardCode: cardCode.trim(),
      });
      const outcome = await submitSaleRegistration(ACCOUNT_SETUP_AUCTION_ID, nonce);
      if (outcome === 'registered') {
        setShowCardForm(false);
        setCardNumber('');
        setExpMonth('');
        setExpYear('');
        setCardCode('');
        toast.success('Card verified — your account is ready to bid.');
        navigate('/browse');
      } else {
        toast.error('Card was not accepted. Please try another card.');
      }
    } catch (e: any) {
      console.error(e);
      toast.error(e?.message || 'Card verification failed');
    } finally {
      setCardSubmitting(false);
    }
  };

  if (!currentUser) {
    return null; // Will redirect via useEffect
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-2xl mx-auto px-4">
        <div className="card">
          <h1 className="text-3xl font-bold mb-2">Complete Your Bidder Profile</h1>
          <p className="text-gray-600 mb-6">
            Add your contact and billing information to start bidding
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Account Information (Read-Only) */}
            <div>
              <h2 className="text-xl font-bold mb-4">Account Information</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-1 text-gray-600">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={currentUser.email || ''}
                    className="input bg-gray-100 cursor-not-allowed"
                    disabled
                    readOnly
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    ✓ Confirmed
                  </p>
                </div>

                {userProfile?.bidderNumber && (
                  <div>
                    <label className="block text-sm font-semibold mb-1 text-gray-600">
                      Bidder Number
                    </label>
                    <input
                      type="text"
                      value={userProfile.bidderNumber}
                      className="input bg-gray-100 cursor-not-allowed font-mono"
                      disabled
                      readOnly
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      ✓ Auto-generated
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Personal Information */}
            <div>
              <h2 className="text-xl font-bold mb-4">Personal Information</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="displayName"
                    value={formData.displayName}
                    onChange={handleChange}
                    className="input"
                    placeholder="John Doe"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    className="input"
                    placeholder="(555) 555-5555"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Billing Address */}
            <div>
              <h2 className="text-xl font-bold mb-4">Billing Address</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-1">
                    Address Line 1 *
                  </label>
                  <input
                    type="text"
                    name="addressLine1"
                    value={formData.addressLine1}
                    onChange={handleChange}
                    className="input"
                    placeholder="123 Main Street"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1">
                    Address Line 2
                  </label>
                  <input
                    type="text"
                    name="addressLine2"
                    value={formData.addressLine2}
                    onChange={handleChange}
                    className="input"
                    placeholder="Apt 4B"
                  />
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-semibold mb-1">
                      City *
                    </label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="input"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-1">
                      State *
                    </label>
                    <input
                      type="text"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      className="input"
                      placeholder="PA"
                      maxLength={2}
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-1">
                      ZIP Code *
                    </label>
                    <input
                      type="text"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleChange}
                      className="input"
                      placeholder="19464"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-1">
                    Country *
                  </label>
                  <select
                    name="country"
                    value={formData.country}
                    onChange={handleChange}
                    className="input"
                    required
                  >
                    <option value="United States">United States</option>
                    <option value="Canada">Canada</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Next Steps Info */}
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800 font-semibold mb-1">
                📋 Next Step: Payment Authorization
              </p>
              <p className="text-xs text-yellow-700">
                After completing this profile, you'll need to authorize a credit card before bidding. This verifies your identity and ensures payment capability.
              </p>
            </div>

            {/* Submit */}
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => navigate('/auctions')}
                className="btn-secondary flex-1"
                disabled={loading}
              >
                Skip for Now
              </button>
              <button
                type="submit"
                className="btn-primary flex-1"
                disabled={loading}
              >
                {loading ? 'Saving Profile...' : 'Complete Profile'}
              </button>
            </div>
          </form>
        </div>
      </div>

      {showCardForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-bold mb-1">Verify a card to finish</h2>
            <p className="text-xs text-gray-500 mb-4">
              A $0 authorization confirms your card — you are not charged. Your card
              details go directly to our processor and never touch our servers.
            </p>
            <label className="block text-sm font-medium text-gray-700 mb-1">Card number</label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="cc-number"
              placeholder="4111 1111 1111 1111"
              value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value)}
              className="w-full mb-3 px-3 py-2 border rounded text-sm"
            />
            <div className="flex gap-3 mb-3">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Exp. month</label>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="MM"
                  value={expMonth}
                  onChange={(e) => setExpMonth(e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm"
                />
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Exp. year</label>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="YYYY"
                  value={expYear}
                  onChange={(e) => setExpYear(e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm"
                />
              </div>
              <div className="w-24">
                <label className="block text-sm font-medium text-gray-700 mb-1">CVV</label>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="cc-csc"
                  placeholder="123"
                  value={cardCode}
                  onChange={(e) => setCardCode(e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={handleCardSubmit}
                disabled={cardSubmitting}
                className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {cardSubmitting ? 'Verifying…' : 'Verify & finish'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCardForm(false);
                  navigate('/browse');
                }}
                disabled={cardSubmitting}
                className="px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 disabled:opacity-50"
              >
                Skip for now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BidderRegistration;